import { parseAmbiente, parseCodDoc, type Ambiente, type CodDoc } from '@/domain/enums';
import { estadoFromServer, type Estado } from '@/domain/status';
import type { Linea } from '@/domain/totals';

export interface NombreValor {
  nombre: string;
  valor: string;
}

export interface Pago {
  formaPago: string;
  total: number;
}

export interface Cliente {
  id: string | null;
  appId: string | null;
  identificacion: string;
  razonSocial: string;
  tipoIdentificacion: string;
  direccion: string | null;
  email: string | null;
  telefono: string | null;
  /** SRI country código (tabla 16), e.g. "110". Not ISO. Used for exports. */
  pais: string | null;
  /** Free text; becomes puertoDestino on an export. */
  ciudad: string | null;
}

export interface Producto {
  id: string | null;
  appId: string | null;
  codigoPrincipal: string;
  codigoAuxiliar: string | null;
  descripcion: string;
  precioUnitario: number;
  tarifaIvaOpcion: string;
  ivaRate: number;
  iceCodigoPorcentaje: string | null;
  iceTarifa: number | null;
  infoAdicional: NombreValor[];
}

export interface Factura {
  /** Server id (may equal appId on some rows). */
  id: string;
  appId: string | null;
  /** Best key for routes/matching: appId, falling back to id. */
  key: string;
  numero: string;
  establecimiento: string;
  puntoEmision: string;
  secuencial: string;
  codDoc: CodDoc;
  status: string | null;
  sriEstado: string | null;
  estado: Estado;
  clienteRazonSocial: string;
  clienteIdentificacion: string;
  clienteTipoIdentificacion: string | null;
  clienteDireccion: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;
  total: number | null;
  propina: number;
  claveAcceso: string | null;
  fechaEmision: string | null;
  fechaAutorizacion: string | null;
  createdAt: string | null;
  ambiente: Ambiente | null;
  items: Linea[];
  pagos: Pago[];
  infoAdicional: NombreValor[];
  mensajes: string | null;
  /** Sustento fields for NC/ND/retención. */
  codDocModificado: string | null;
  numDocModificado: string | null;
  fechaEmisionDocSustento: string | null;
  motivo: string | null;
  raw: Record<string, unknown>;
}

export interface Emisor {
  ruc: string;
  razonSocial: string;
  nombreComercial: string | null;
  direccionMatriz: string | null;
  obligadoContabilidad: 'SI' | 'NO';
  ambiente: Ambiente;
  isSignatureConfigured: boolean;
  certificateUploadedAt: string | null;
  validationStatus: CertValidationStatus;
  validatedAt: string | null;
  certificateExpiresAt: string | null;
  logoUploadedAt: string | null;
  logoUrl: string | null;
  establecimientos: Establecimiento[];
}

export type CertValidationStatus = 'pending' | 'validating' | 'valid' | 'mismatch' | 'invalid' | 'invalidated' | 'unknown';
export const CERT_BLOCKING: CertValidationStatus[] = ['mismatch', 'invalid', 'invalidated'];

export interface Establecimiento {
  id: string;
  codigo: string;
  nombreComercial: string | null;
  direccion: string | null;
  isDefault: boolean;
  puntos: PuntoEmision[];
}

export interface PuntoEmision {
  id: string;
  establecimientoId: string | null;
  establecimientoCodigo: string | null;
  codigo: string;
  descripcion: string | null;
  isDefault: boolean;
}

/**
 * A tier change Stripe has scheduled but not applied — in practice, a downgrade waiting for the
 * renewal date.
 *
 * Read back from Stripe on every request rather than cached, because the customer can cancel it
 * from the billing portal. A stale copy would keep promising a change that is no longer coming,
 * which is worse than showing nothing.
 */
export interface PendingChange {
  planCode: string | null;
  priceId: string | null;
  effectiveAt: string | null;
}

export interface Subscription {
  /** When the card is charged, or the last day of access once cancelled. */
  renewsAt: string | null;
  /** Missing means true: an active subscription renews unless it was cancelled. */
  willRenew: boolean;
  /** e.g. "year". Unused so far; read it for a "PLAN PRO · anual" badge. */
  billingPeriod: string | null;
}

export interface Usage {
  plan: string;
  resource: string;
  /** null when the account is unlimited — deliberately, so nothing can subtract from a sentinel. */
  quota: number | null;
  used: number;
  /** null when the account is unlimited. */
  remaining: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** When the metered allowance refills. NOT the billing date — never share a verb with it. */
  resetsAt: string | null;
  /** null on a free or expired account. */
  subscription: Subscription | null;
  isSubscribed: boolean;
  /** A scheduled tier change, or null when nothing is pending. */
  pendingChange: PendingChange | null;
  /** No cap on this account: never count, never gate, never offer an upgrade. */
  unlimited: boolean;
  /** Whether to show the paid plan at all. Never to a subscriber, never to an unlimited account. */
  canUpgrade: boolean;
  hasRemaining: boolean;
}

export interface Passkey {
  id: number | string;
  deviceName: string | null;
  platform: string | null;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/* ---------- tolerant readers ---------- */

type R = Record<string, unknown>;

/**
 * A JSON object, or a loud failure.
 *
 * CloudFront's custom error responses (403/404 → index.html, status 200) are distribution-wide,
 * so a backend 403 or 404 reaches the browser as the SPA's own HTML with a 200 — verified in
 * production. The body then parses as a string, and reading fields off it yields undefined
 * everywhere: an empty key list, a plan change reported as "scheduled". That is the
 * failed-read-as-empty pattern this project already shipped once with the invented `/sri`
 * route. Fail where the shape is wrong instead. See the warning in the README's API section.
 */
export function expectJson(v: unknown, what: string): R {
  if (Array.isArray(v)) return { data: v };
  if (v && typeof v === 'object') return v as R;
  throw new Error(`El servidor no devolvió ${what}. Puede que la función no esté disponible en este servidor.`);
}

/**
 * A documented `204 No Content`, or a loud failure.
 *
 * `http` returns `null` for an empty body, so anything else came from somewhere that is not the
 * endpoint we asked for — in practice the SPA's own HTML, rewritten over a 403 or 404 by the
 * distribution. Revoking a leaked production key is the one action where a false confirmation is
 * genuinely dangerous: the toast says "Clave revocada" while the key is still live.
 */
export function expectNoContent(v: unknown, what: string): void {
  if (v === null || v === undefined || v === '') return;
  throw new Error(`El servidor no confirmó ${what}. Vuelve a cargar la lista y compruébalo antes de continuar.`);
}

export function pick(o: R | null | undefined, ...keys: string[]): unknown {
  if (!o) return undefined;
  for (const k of keys) {
    if (k.includes('.')) {
      const v = k.split('.').reduce<unknown>((acc, part) => (acc && typeof acc === 'object' ? (acc as R)[part] : undefined), o);
      if (v !== undefined && v !== null) return v;
    } else if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
}
export function str(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}
export function num(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
export function bool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  const s = String(v ?? '').toLowerCase();
  return s === 'true' || s === '1' || s === 'si' || s === 'sí' || s === 'yes';
}
export function arr(v: unknown): R[] {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as R[]) : [];
}

export function normNombreValor(v: unknown): NombreValor[] {
  if (Array.isArray(v)) {
    return arr(v)
      .map((x) => ({ nombre: str(pick(x, 'nombre', 'name', 'key')) ?? '', valor: str(pick(x, 'valor', 'value')) ?? '' }))
      .filter((x) => x.nombre || x.valor);
  }
  if (v && typeof v === 'object') return Object.entries(v as R).map(([nombre, valor]) => ({ nombre, valor: String(valor ?? '') }));
  return [];
}

/** SRI `mensajes`: string | [{identificador,mensaje,informacionAdicional,tipo}] | []. Empty → null. */
export function formatMensajes(m: unknown): string | null {
  if (m == null) return null;
  if (typeof m === 'string') return m.trim() ? m.trim() : null;
  if (Array.isArray(m)) {
    const lines = m
      .map((x) => {
        if (typeof x === 'string') return x.trim();
        if (x && typeof x === 'object') {
          const r = x as R;
          return [str(pick(r, 'identificador', 'codigo')), str(pick(r, 'mensaje', 'message')), str(pick(r, 'informacionAdicional', 'info'))].filter(Boolean).join(' · ');
        }
        return '';
      })
      .filter(Boolean);
    return lines.length ? lines.join('\n') : null;
  }
  if (typeof m === 'object') {
    const r = m as R;
    const one = [str(pick(r, 'identificador', 'codigo')), str(pick(r, 'mensaje', 'message')), str(pick(r, 'informacionAdicional', 'info'))].filter(Boolean).join(' · ');
    return one || null;
  }
  return null;
}

export function normCliente(r: R): Cliente {
  return {
    id: str(pick(r, 'id', 'serverId', 'server_id')),
    appId: str(pick(r, 'appId', 'app_id')),
    identificacion: str(pick(r, 'identificacion')) ?? '',
    razonSocial: str(pick(r, 'razonSocial', 'razon_social', 'nombre')) ?? '',
    tipoIdentificacion: str(pick(r, 'tipoIdentificacion', 'tipo_identificacion')) ?? '05',
    direccion: str(pick(r, 'direccion')),
    email: str(pick(r, 'email')),
    telefono: str(pick(r, 'telefono', 'phone')),
    pais: str(pick(r, 'pais', 'paisCodigo', 'pais_codigo')),
    ciudad: str(pick(r, 'ciudad', 'city')),
  };
}

export function normProducto(r: R): Producto {
  const ice = (pick(r, 'ice') as R | undefined) ?? undefined;
  return {
    id: str(pick(r, 'id', 'serverId', 'server_id')),
    appId: str(pick(r, 'appId', 'app_id')),
    codigoPrincipal: str(pick(r, 'codigoPrincipal', 'codigo_principal', 'codigo')) ?? '',
    codigoAuxiliar: str(pick(r, 'codigoAuxiliar', 'codigo_auxiliar')),
    descripcion: str(pick(r, 'descripcion', 'nombre')) ?? '',
    precioUnitario: num(pick(r, 'precioUnitario', 'precio_unitario', 'precio')) ?? 0,
    tarifaIvaOpcion: str(pick(r, 'tarifaIvaOpcion', 'tarifa_iva_opcion')) ?? 'IVA_15',
    ivaRate: num(pick(r, 'ivaRate', 'iva_rate')) ?? 0,
    iceCodigoPorcentaje: str(pick(r, 'iceCodigoPorcentaje', 'ice_codigo_porcentaje') ?? pick(ice, 'codigoPorcentaje')),
    iceTarifa: num(pick(r, 'iceTarifa', 'ice_tarifa') ?? pick(ice, 'tarifa')),
    infoAdicional: normNombreValor(pick(r, 'infoAdicional', 'info_adicional')),
  };
}

export function normLinea(r: R): Linea {
  const ice = pick(r, 'ice') as R | undefined;
  const iceCod = str(pick(r, 'iceCodigoPorcentaje') ?? pick(ice, 'codigoPorcentaje'));
  const iceTar = num(pick(r, 'iceTarifa') ?? pick(ice, 'tarifa'));
  return {
    codigoPrincipal: str(pick(r, 'codigoPrincipal', 'codigo_principal', 'codigo')) ?? '',
    codigoAuxiliar: str(pick(r, 'codigoAuxiliar', 'codigo_auxiliar')),
    descripcion: str(pick(r, 'descripcion', 'nombre')) ?? '',
    cantidad: num(pick(r, 'cantidad', 'quantity')) ?? 1,
    precioUnitario: num(pick(r, 'precioUnitario', 'precio_unitario', 'precio')) ?? 0,
    descuento: num(pick(r, 'descuento')) ?? 0,
    tarifaIvaOpcion: str(pick(r, 'tarifaIvaOpcion', 'tarifa_iva_opcion')) ?? 'IVA_15',
    ivaRate: num(pick(r, 'ivaRate', 'iva_rate')),
    tarifaEspecial: num(pick(r, 'tarifaEspecial', 'tarifa_especial')),
    ice: iceCod ? { codigoPorcentaje: iceCod, tarifa: iceTar ?? 0 } : null,
    infoAdicional: normNombreValor(pick(r, 'infoAdicional', 'info_adicional')),
  };
}

export function normPagos(v: unknown): Pago[] {
  return arr(v)
    .map((p) => ({ formaPago: str(pick(p, 'formaPago', 'forma_pago')) ?? '01', total: num(pick(p, 'total', 'valor')) ?? 0 }))
    .filter((p) => p.total > 0 || true);
}

export function splitNumero(numero: string): { establecimiento: string; puntoEmision: string; secuencial: string } {
  const [e = '', p = '', s = ''] = numero.split('-');
  return { establecimiento: e, puntoEmision: p, secuencial: s };
}

export function normFactura(r: R): Factura {
  const payload = (pick(r, 'metadata.payload', 'payload') as R | undefined) ?? undefined;
  const comprobante = (pick(payload, 'comprobante') as R | undefined) ?? undefined;
  const cliente = (pick(r, 'cliente') as R | undefined) ?? (pick(payload, 'cliente') as R | undefined) ?? undefined;
  const id = str(pick(r, 'id', 'serverId', 'server_id')) ?? '';
  const appId = str(pick(r, 'appId', 'app_id'));
  const claveAcceso = str(pick(r, 'claveAcceso', 'clave_acceso', 'clave'));
  let numero = str(pick(r, 'numero', 'number')) ?? '';
  const estab = str(pick(r, 'establecimiento', 'estab') ?? pick(comprobante, 'establecimiento'));
  const punto = str(pick(r, 'puntoEmision', 'punto_emision', 'punto') ?? pick(comprobante, 'puntoEmision'));
  const sec = str(pick(r, 'secuencial') ?? pick(comprobante, 'secuencial'));
  if (!numero && estab && punto && sec) numero = `${estab}-${punto}-${sec}`;
  const parts = splitNumero(numero);
  const status = str(pick(r, 'status', 'lifecycle'));
  const sriEstado = str(pick(r, 'sriEstado', 'sri_estado', 'estado'));
  const items = arr(pick(r, 'items', 'detalles') ?? pick(payload, 'detalles')).map(normLinea);
  const totales = pick(r, 'totales') as R | undefined;
  return {
    id: id || appId || '',
    appId,
    key: appId || id,
    numero,
    establecimiento: estab ?? parts.establecimiento,
    puntoEmision: punto ?? parts.puntoEmision,
    secuencial: sec ?? parts.secuencial,
    codDoc: parseCodDoc(pick(r, 'voucherType', 'voucher_type', 'tipoComprobante', 'tipo_comprobante', 'codDoc', 'cod_doc', 'tipo') ?? pick(comprobante, 'tipoComprobante')),
    status,
    sriEstado,
    estado: estadoFromServer(status, sriEstado),
    clienteRazonSocial: str(pick(r, 'clienteRazonSocial', 'cliente_razon_social') ?? pick(cliente, 'razonSocial', 'razon_social')) ?? '',
    clienteIdentificacion: str(pick(r, 'clienteIdentificacion', 'cliente_identificacion') ?? pick(cliente, 'identificacion')) ?? '',
    clienteTipoIdentificacion: str(pick(r, 'clienteTipoIdentificacion', 'cliente_tipo_identificacion') ?? pick(cliente, 'tipoIdentificacion')),
    clienteDireccion: str(pick(r, 'clienteDireccion', 'cliente_direccion') ?? pick(cliente, 'direccion')),
    clienteEmail: str(pick(r, 'clienteEmail', 'cliente_email') ?? pick(cliente, 'email')),
    clienteTelefono: str(pick(r, 'clienteTelefono', 'cliente_telefono') ?? pick(cliente, 'telefono')),
    total: num(pick(r, 'total', 'importeTotal', 'importe_total')),
    propina: num(pick(r, 'propina') ?? pick(totales, 'propina')) ?? 0,
    claveAcceso,
    fechaEmision: str(pick(r, 'fechaEmision', 'fecha_emision') ?? pick(comprobante, 'fechaEmision')),
    fechaAutorizacion: str(pick(r, 'fechaAutorizacion', 'fecha_autorizacion', 'authorizedAt', 'authorized_at')),
    createdAt: str(pick(r, 'createdAt', 'created_at', 'fecha')),
    ambiente: parseAmbiente(pick(r, 'ambiente'), claveAcceso),
    items,
    pagos: normPagos(pick(r, 'pagos') ?? pick(payload, 'pagos')),
    infoAdicional: normNombreValor(pick(r, 'infoAdicional', 'info_adicional') ?? pick(payload, 'infoAdicional')),
    mensajes: formatMensajes(pick(r, 'mensajes', 'mensaje', 'sriMensajes', 'sri_mensajes')),
    codDocModificado: str(pick(r, 'codDocModificado') ?? pick(comprobante, 'codDocModificado')),
    numDocModificado: str(pick(r, 'numDocModificado') ?? pick(comprobante, 'numDocModificado')),
    fechaEmisionDocSustento: str(pick(r, 'fechaEmisionDocSustento') ?? pick(comprobante, 'fechaEmisionDocSustento')),
    motivo: str(pick(r, 'motivo') ?? pick(comprobante, 'motivo')),
    raw: r,
  };
}

export function normPunto(r: R, estab?: { id: string; codigo: string }): PuntoEmision {
  return {
    id: str(pick(r, 'id')) ?? '',
    establecimientoId: str(pick(r, 'establecimientoId', 'establecimiento_id', 'establecimiento.id')) ?? estab?.id ?? null,
    establecimientoCodigo: str(pick(r, 'establecimientoCodigo', 'establecimiento_codigo', 'establecimiento.codigo')) ?? estab?.codigo ?? null,
    codigo: str(pick(r, 'codigo')) ?? '',
    descripcion: str(pick(r, 'descripcion')),
    isDefault: bool(pick(r, 'isDefault', 'is_default')),
  };
}

export function normEstablecimiento(r: R): Establecimiento {
  const id = str(pick(r, 'id')) ?? '';
  const codigo = str(pick(r, 'codigo')) ?? '';
  return {
    id,
    codigo,
    nombreComercial: str(pick(r, 'nombreComercial', 'nombre_comercial', 'nombre')),
    direccion: str(pick(r, 'direccion')),
    isDefault: bool(pick(r, 'isDefault', 'is_default')),
    puntos: arr(pick(r, 'puntosEmision', 'puntos_emision', 'puntos', 'emissionPoints', 'emission_points', 'points')).map((p) => normPunto(p, { id, codigo })),
  };
}

export function normCertStatus(v: unknown): CertValidationStatus {
  const s = String(v ?? '').toLowerCase();
  return (['pending', 'validating', 'valid', 'mismatch', 'invalid', 'invalidated'] as const).find((x) => x === s) ?? 'unknown';
}

export function normEmisor(r: R): Emisor {
  return {
    ruc: str(pick(r, 'ruc')) ?? '',
    razonSocial: str(pick(r, 'razonSocial', 'razon_social')) ?? '',
    nombreComercial: str(pick(r, 'nombreComercial', 'nombre_comercial')),
    direccionMatriz: str(pick(r, 'direccionMatriz', 'direccion_matriz')),
    obligadoContabilidad: bool(pick(r, 'obligadoContabilidad', 'obligado_contabilidad')) ? 'SI' : 'NO',
    ambiente: parseAmbiente(pick(r, 'ambiente')) ?? '1',
    isSignatureConfigured: bool(pick(r, 'isSignatureConfigured', 'is_signature_configured')),
    certificateUploadedAt: str(pick(r, 'certificateUploadedAt', 'certificate_uploaded_at')),
    validationStatus: normCertStatus(pick(r, 'validationStatus', 'validation_status')),
    validatedAt: str(pick(r, 'validatedAt', 'validated_at')),
    certificateExpiresAt: str(pick(r, 'certificateExpiresAt', 'certificate_expires_at')),
    logoUploadedAt: str(pick(r, 'logoUploadedAt', 'logo_uploaded_at')),
    logoUrl: str(pick(r, 'logoUrl', 'logo_url')),
    establecimientos: arr(pick(r, 'establecimientos')).map(normEstablecimiento),
  };
}

export function normUsage(r: R): Usage {
  // `quota` and `remaining` are null on an unlimited account, by design, so that nothing can print
  // a sentinel or subtract from one. Keep the null rather than coercing it to 0.
  const quotaRaw = pick(r, 'quota');
  const quota = quotaRaw === null || quotaRaw === undefined ? null : num(quotaRaw);
  const used = num(pick(r, 'used')) ?? 0;
  const remainingRaw = pick(r, 'remaining');
  const plan = str(pick(r, 'plan')) ?? 'free';
  // The server always sends this flag, on both plans, so it decides. The quota fallback only
  // covers an older response that predates it — never an empty body, which says nothing at all.
  const sub = pick(r, 'subscription');
  const subRow = sub && typeof sub === 'object' ? (sub as R) : null;
  const willRenewRaw = subRow ? pick(subRow, 'willRenew', 'will_renew') : undefined;
  const pendRow = pick(r, 'pending_change', 'pendingChange');
  const pend = pendRow && typeof pendRow === 'object' ? (pendRow as R) : null;
  const flag = pick(r, 'unlimited', 'is_unlimited', 'isUnlimited');
  const unlimited = flag === null || flag === undefined ? quota !== null && quota <= 0 : bool(flag);
  const remaining =
    remainingRaw === null || remainingRaw === undefined ? (unlimited || quota === null ? null : quota - used) : num(remainingRaw);
  return {
    plan,
    resource: str(pick(r, 'resource')) ?? 'invoices',
    quota,
    used,
    remaining,
    periodStart: str(pick(r, 'periodStart', 'period_start')),
    periodEnd: str(pick(r, 'periodEnd', 'period_end')),
    resetsAt: str(pick(r, 'resetsAt', 'resets_at')),
    subscription: subRow
      ? {
          renewsAt: str(pick(subRow, 'renewsAt', 'renews_at')),
          // Absent means true — an active subscription renews unless it was cancelled.
          willRenew: willRenewRaw === null || willRenewRaw === undefined ? true : bool(willRenewRaw),
          billingPeriod: str(pick(subRow, 'billingPeriod', 'billing_period')),
        }
      : null,
    pendingChange: pend
      ? {
          planCode: str(pick(pend, 'plan_code', 'planCode')),
          priceId: str(pick(pend, 'price_id', 'priceId')),
          effectiveAt: str(pick(pend, 'effective_at', 'effectiveAt')),
        }
      : null,
    isSubscribed: plan.toLowerCase() !== 'free',
    unlimited,
    canUpgrade: plan.toLowerCase() === 'free' && !unlimited,
    hasRemaining: unlimited || (remaining ?? 0) > 0,
  };
}

export function normPasskey(r: R): Passkey {
  return {
    id: (pick(r, 'id') as number | string) ?? '',
    deviceName: str(pick(r, 'device_name', 'deviceName')),
    platform: str(pick(r, 'platform')),
    deviceType: str(pick(r, 'device_type', 'deviceType')),
    backedUp: bool(pick(r, 'backed_up', 'backedUp')),
    createdAt: str(pick(r, 'created_at', 'createdAt')),
    lastUsedAt: str(pick(r, 'last_used_at', 'lastUsedAt')),
  };
}
